import {
  Component,
  Prop,
  h,
  Host,
  Element,
  Listen,
  Event,
  EventEmitter,
  Watch,
} from '@stencil/core';
import { SortDirection } from './HpTableTypes';
import { Hp6TableActionsType, Hp6TableActionEventDetail } from './HpTableActionEvent';
import Key from '../../../capabilities/io/Key';
import stringTemplate from './string-template';

/**
 * @description Subcomponent of table
 * @since 5.36.1
 * @version figma-v1
 * @ignore
 */
@Component({
  tag: 'hp6-table-header-panel',
  styleUrl: 'table-header-panel.scss',
  shadow: false, // Has no slot and is not a standalone component, so no need for shadow DOM
})
export class TableHeaderPanel {
  /**
   * If the datepicker calendar is visible/open
   */
  @Prop({ mutable: true, reflect: true }) isVisible: boolean = false;

  /**
   * The index of the column to which this header panel is attached
   */
  @Prop() columnIndex: number;

  /**
   * The innerHtml of the <th />
   */
  @Prop() columnHeadingText: string;

  /**
   * An array of filter values to display in JSON string format
   */
  @Prop() filterValuesJson: string = null;

  /**
   * Boolean if more than one filter is selectable in this
   */
  @Prop() filterValuesMultiple: boolean = false;

  /**
   * A json string that is an object describing the currently active filters in this
   */
  @Prop({ mutable: true, reflect: true }) filterValuesActiveJson: string = null;

  /**
   * Determines if sort section of filter panel is to be displayed
   */
  @Prop() isSortable: boolean = false;

  /**
   * If this column is currently being sorted
   * Will be null if sorting is not active for this column
   */
  @Prop({ reflect: true, mutable: true })
  sortDirection: SortDirection | null;

  /**
   * The id attribute of the table. Used for a11y
   */
  @Prop() tableId: string;

  /**
   * Text inside this that will be read by the screen reader and not displayed
   */
  @Prop() textScreenReader: string;

  /**
   * The text next to the sort radio button
   */
  @Prop() textSortDirectionAscending: string;

  /**
   * The text next to the sort radio button
   */
  @Prop() textSortDirectionDescending: string;

  /**
   * The text above the sort radio buttons
   */
  @Prop() textLabelSort: string;

  /**
   * The text above the filter radio buttons/checkboxes
   */
  @Prop() textLabelFilter: string;

  /**
   * The text in the Clear buttons
   */
  @Prop() textLabelClear: string;

  /**
   * The aria-label on the clear sort buttons
   */
  @Prop() textLabelClearSortAriaLabel: string;

  /**
   * The aria-label on the clear filter buttons
   */
  @Prop() textLabelClearFilterAriaLabel: string;

  /**
   * The text in the Close button
   */
  @Prop() textLabelClose: string;

  /**
   * The host element in this
   */
  @Element() host: HTMLElement;

  /**
   * Emitted when sort, filter, or clear is clicked or activated
   *
   * @see https://stenciljs.com/docs/events#event-decorator
   */
  @Event({ bubbles: true }) hp6TableActionEvent: EventEmitter<Hp6TableActionEventDetail>;

  /**
   * constructor()
   */
  constructor() {
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.onClickClearSort = this.onClickClearSort.bind(this);
    this.onChangeSort = this.onChangeSort.bind(this);
    this.onClickClearFilter = this.onClickClearFilter.bind(this);
    this.onChangeFilter = this.onChangeFilter.bind(this);
  }

  /**
   * Stencil.js lifecycle
   */
  componentDidLoad() {
    window.addEventListener('mousedown', this.handleClickOutside);
  }

  /**
   * Stencil.js lifecycle
   */
  disconnectedCallback() {
    window.removeEventListener('mousedown', this.handleClickOutside);
  }

  /**
   * The DOM element that was used to trigger the display of the label.
   * Will regain focus after this hp-table-header-panel is no longer visible.
   */
  private focusRestoreTarget: Element;

  /**
   * Close when something other than this component instance is clicked
   *
   * NOTE: This method will be called for each instance of this
   *
   * @param event
   */
  private handleClickOutside(event: Event) {
    const target = event.target as HTMLElement;
    const { columnIndex } = this;

    // selector that is hopefully unique to this
    const selector = `hp6-table-header-panel[column-index="${columnIndex}"]`;

    const tagName = target.tagName.toUpperCase();

    // if the element that was clicked is this instance's previous sibling button return and ignore
    // hiding of this is handled in table
    if (tagName === 'BUTTON') {
      const instance = target.parentNode.querySelector(selector);

      if (instance) {
        return;
      }
    }

    let current = target as HTMLElement;

    if (!current || !current.tagName) {
      return;
    }

    // keep getting parent node until this is found
    while (current.tagName.toLocaleLowerCase() !== 'hp6-table-header-panel') {
      // @ts-expect-error Type 'ParentNode' is missing the following properties from type
      // 'HTMLTableCellElement': abbr, align, axis, bgColor, and 233 more.ts(2740)
      current = current.parentNode;

      if (!current) {
        break;
      }

      if (!current.parentNode) {
        break;
      }

      if (!current.tagName) {
        break;
      }

      if (current.tagName.toLocaleLowerCase() === 'hp6-table-header-panel') {
        if (current.getAttribute('column-index').toString() === columnIndex.toString()) {
          return;
        }
      }
    }

    this.isVisible = false;
  }

  /**
   * Watcher that maps initial visible trigger element, then
   * returns focus once the hp-table-haeder-panel is closed via esc
   */
  @Watch('isVisible')
  onVisibleChange(isVisible: boolean) {
    if (isVisible === true) {
      this.focusRestoreTarget = document.activeElement;
    } else if (this.focusRestoreTarget && this.focusRestoreTarget instanceof HTMLElement) {
      this.focusRestoreTarget.focus();
    }
  }

  /**
   * Keyup listener all keyboard events.  Close when esc is pressed.
   */
  @Listen('keyup', { target: 'body' })
  keyHandlerUp(event: KeyboardEvent) {
    const { isVisible } = this;

    if (isVisible && Key.esc.didFire(event)) {
      this.isVisible = false;
    }
  }

  /**
   * Clear sort for this column
   *
   * @param event Event
   */
  onClickClearSort(event: Event): void {
    event.stopPropagation();

    const { columnIndex } = this;

    const e = new Hp6TableActionEventDetail(event, {
      action: Hp6TableActionsType.SORT_CLEAR,
      columnIndex,
    });

    this.hp6TableActionEvent.emit(e);

    this.sortDirection = null;

    (this.host.querySelector(`#hp-table-header-panel-sort-${columnIndex}`) as HTMLElement).focus();
  }

  /**
   * Change sort direction for this column
   *
   * @param event Event
   */
  onChangeSort(event: Event): void {
    event.stopPropagation();

    const { target } = event;
    const { value } = target as HTMLFormElement;
    const { columnIndex } = this;

    const e = new Hp6TableActionEventDetail(event, {
      action: Hp6TableActionsType.SORT,
      columnIndex,
      value,
    });

    this.sortDirection = value;
    this.hp6TableActionEvent.emit(e);
  }

  /**
   * Clear all filters for this column
   *
   * @param event Event
   */
  onClickClearFilter(event: Event): void {
    event.stopPropagation();

    const { columnIndex } = this;

    const e = new Hp6TableActionEventDetail(event, {
      action: Hp6TableActionsType.FILTER_CLEAR,
      columnIndex,
    });

    this.hp6TableActionEvent.emit(e);

    this.filterValuesActiveJson = null;

    (
      this.host.querySelector(`#hp-table-header-panel-filter-${columnIndex}`) as HTMLElement
    ).focus();
  }

  /**
   * Add or remove a filter for this column
   *
   * @param event Event
   */
  onChangeFilter(event: Event): void {
    event.stopPropagation();

    const { target } = event;
    const { value, checked } = target as HTMLFormElement;
    const { columnIndex, filterValuesMultiple } = this;

    if (filterValuesMultiple) {
      if (checked) {
        const e = new Hp6TableActionEventDetail(event, {
          action: Hp6TableActionsType.FILTER_ADD,
          columnIndex,
          value,
        });

        this.hp6TableActionEvent.emit(e);
      } else {
        const e = new Hp6TableActionEventDetail(event, {
          action: Hp6TableActionsType.FILTER_REMOVE,
          columnIndex,
          value,
        });

        this.hp6TableActionEvent.emit(e);
      }
    } else {
      const e = new Hp6TableActionEventDetail(event, {
        action: Hp6TableActionsType.FILTER_REPLACE,
        columnIndex,
        value,
      });

      this.hp6TableActionEvent.emit(e);
    }
  }

  /**
   * render()
   */
  render() {
    const {
      isSortable,
      filterValuesJson,
      filterValuesMultiple,
      filterValuesActiveJson,
      columnIndex,
      isVisible,
      sortDirection,
      columnHeadingText,
    } = this;

    const filterValuesArray = JSON.parse(filterValuesJson);
    const filterValuesActive = JSON.parse(filterValuesActiveJson) || [];

    const clearButtonVisibleSort = !!sortDirection;
    const clearClassSort = clearButtonVisibleSort ? 'is-visible' : '';

    let clearButtonVisibleFilter = false;

    if (filterValuesArray.length > 0) {
      clearButtonVisibleFilter = !!filterValuesActive.length;
    }

    const clearClassFilter = clearButtonVisibleFilter ? 'is-visible' : '';

    const labelledById = `hp-table-header-panel-labelled-by-${columnIndex}`;

    const {
      textScreenReader,
      textSortDirectionAscending,
      textSortDirectionDescending,
      textLabelSort,
      textLabelFilter,
      textLabelClear,
      textLabelClearSortAriaLabel,
      textLabelClearFilterAriaLabel,
      textLabelClose,
      tableId,
    } = this;

    const textScreenReaderReplaced = stringTemplate(textScreenReader, {
      columnHeadingText,
    });

    return (
      <Host
        role="dialog"
        aria-modal="true"
        aria-hidden={isVisible ? 'false' : 'true'}
        aria-labelledby={labelledById} // TODO: needed?
      >
        <hp6-screen-reader-only>
          <span id={labelledById}>{textScreenReaderReplaced}</span>
        </hp6-screen-reader-only>

        <hp6-focus-trap is-active={isVisible}>
          {isSortable && (
            <div class="panel-section">
              <div class="panel-form">
                <form
                  id={`hp-table-header-panel-sort[${columnIndex}]`}
                  onChange={this.onChangeSort}
                >
                  <hp6-fieldset>
                    <fieldset id={`hp-table-header-panel-sort-${tableId}-${columnIndex}`}>
                      <legend>{textLabelSort}</legend>

                      <hp6-radio-button-decorator>
                        <input
                          type="radio"
                          name="sort-direction"
                          id={`hp-table-header-panel-sort-direction-${columnIndex}-${SortDirection.ASCENDING}`}
                          checked={sortDirection === SortDirection.ASCENDING}
                          value={SortDirection.ASCENDING}
                        />
                        <label
                          htmlFor={`hp-table-header-panel-sort-direction-${columnIndex}-${SortDirection.ASCENDING}`}
                        >
                          {textSortDirectionAscending}
                        </label>
                      </hp6-radio-button-decorator>

                      <hp6-radio-button-decorator>
                        <input
                          type="radio"
                          name="sort-direction"
                          id={`hp-table-header-panel-sort-direction-${columnIndex}-${SortDirection.DESCENDING}`}
                          checked={sortDirection === SortDirection.DESCENDING}
                          value={SortDirection.DESCENDING}
                        />
                        <label
                          htmlFor={`hp-table-header-panel-sort-direction-${columnIndex}-${SortDirection.DESCENDING}`}
                        >
                          {textSortDirectionDescending}
                        </label>
                      </hp6-radio-button-decorator>
                    </fieldset>
                  </hp6-fieldset>
                </form>
              </div>

              <div class="clear-button-container">
                <hp6-button-decorator type-selection="tertiary">
                  <button
                    class={clearClassSort}
                    aria-label={textLabelClearSortAriaLabel}
                    type="button"
                    onClick={this.onClickClearSort}
                  >
                    <hp6-icon name="system-delete" size-selection="sm" />
                    {textLabelClear}
                  </button>
                </hp6-button-decorator>
              </div>
            </div>
          )}

          {filterValuesArray.length > 1 && (
            <div class="panel-section">
              <div class="panel-form">
                <form
                  onChange={this.onChangeFilter}
                  id={`hp-table-header-panel-filter[${columnIndex}]`}
                >
                  <hp6-fieldset-decorator>
                    <fieldset id={`hp-table-header-panel-filter-${tableId}[${columnIndex}]`}>
                      <legend>{textLabelFilter}</legend>

                      {!filterValuesMultiple && (
                        <div>
                          {filterValuesArray.map((filter: string) => {
                            const checked = filterValuesActive.includes(filter);
                            const filterName = `filter-radio-column-index-${columnIndex}`;
                            const elementId = `filter-radio-column-index-${columnIndex}-${filter}`;

                            return (
                              <hp6-radio-button-decorator>
                                <input
                                  type="radio"
                                  name={filterName}
                                  id={elementId}
                                  checked={checked}
                                  value={filter}
                                />
                                <label
                                  htmlFor={`hp-table-header-panel-sort-direction-${columnIndex}-${SortDirection.DESCENDING}`}
                                >
                                  {textSortDirectionDescending}
                                </label>
                              </hp6-radio-button-decorator>
                            );
                          })}
                        </div>
                      )}

                      {filterValuesMultiple && (
                        <div>
                          {filterValuesArray.map((filter: string) => {
                            const checked = filterValuesActive.includes(filter);
                            const filterName = `filter${columnIndex}${filter}`;

                            return (
                              <hp6-checkbox-decorator>
                                <input
                                  type="checkbox"
                                  name={filterName}
                                  id={filterName}
                                  value={filter}
                                  checked={checked}
                                />
                                <label htmlFor={filterName}>{filter}</label>
                              </hp6-checkbox-decorator>
                            );
                          })}
                        </div>
                      )}
                    </fieldset>
                  </hp6-fieldset-decorator>
                </form>
              </div>

              <div class="clear-button-container">
                <hp6-button-decorator type-selection="tertiary">
                  <button
                    class={clearClassFilter}
                    aria-label={textLabelClearFilterAriaLabel}
                    type="button"
                    onClick={this.onClickClearFilter}
                  >
                    <hp6-icon name="system-delete" size-selection="sm" />
                    {textLabelClear}
                  </button>
                </hp6-button-decorator>
              </div>
              <div class="shrink" />
            </div>
          )}

          <div class="panel-section">
            <div class="close-button-container">
              <hp6-button-decorator type-selection="tertiary">
                <button
                  type="button"
                  onClick={(event: Event) => {
                    event.stopPropagation();
                    this.isVisible = false;
                  }}
                >
                  <hp6-icon name="system-close" size-selection="sm" />
                  {textLabelClose}
                </button>
              </hp6-button-decorator>
            </div>
          </div>
        </hp6-focus-trap>
      </Host>
    );
  }
}
